import type { ReactNode } from 'react';

/**
 * Spec 231 — a region of the sheet.
 *
 * Regions TOUCH. The hairline between consecutive ones comes from
 * `.hub-page--dashboard > * + *` in the vendored layout.css, so a region must
 * never carry its own border, margin or radius — doing that reintroduces the
 * card-in-a-tray tell this archetype exists to remove, and gate 231.A.2 fails
 * on exactly that shape.
 */
export function Region({ children, className, ...rest }: { children: ReactNode; className?: string } & Record<string, unknown>) {
  return (
    <section className={`hub-region${className ? ' ' + className : ''}`} {...rest}>
      {children}
    </section>
  );
}

/**
 * The only place a region gets chrome: a heading, a one-line caption, and at
 * most ONE action on the right.
 *
 * `action` is deliberately singular. A head row that accumulates three buttons
 * is a region that has not decided what it is for, and it is also where an
 * icon-only control with no accessible name tends to appear — gate 230.A.4
 * scans Overview's live DOM for exactly that.
 *
 * Bare `<h2>` and `<p>`, with no classes, is correct here and is not an
 * oversight: `.hub-region__head` styles its own descendants (see
 * layout.css). Neither `.hub-subtitle` nor `.hub-caption` exist in the
 * vendored sheets — they were never real classes, so this deliberately does
 * not reach for them.
 */
export function RegionHead({ title, caption, action }: { title: string; caption?: string; action?: ReactNode }) {
  return (
    <div className="hub-region__head">
      <div>
        <h2>{title}</h2>
        {caption ? <p>{caption}</p> : null}
      </div>
      {action ?? null}
    </div>
  );
}
